import { useState } from "react";
import { motion } from "framer-motion";
import { WhatsappLogo, CheckCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AnimatedSection from "@/components/layout/AnimatedSection";
import {
  fadeUp,
  serviceOptions,
  WHATSAPP_NUMBER,
  WHATSAPP_QUOTE_URL
} from "@/data/site";

const QuoteForm = ({ showHeading = true }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    material: "",
    quantity: "",
    service: ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const message = `Olá, vim pelo site e gostaria de um orçamento.%0A%0A*Nome:* ${formData.name}%0A*Telefone:* ${formData.phone}%0A*Tipo de material:* ${formData.material}%0A*Quantidade:* ${formData.quantity}%0A*Acabamento:* ${formData.service}`;

    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  return (
    <AnimatedSection id="orcamento" className="section-padding bg-[#234D89] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#E73336]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E73336]/5 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div variants={fadeUp}>
            {showHeading && (
              <>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E73336] mb-4 block">
                  Orçamento Rápido
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-6 leading-tight">
                  Solicite seu <span className="text-[#E73336]">Orçamento</span> Agora
                </h2>
                <div className="section-divider mb-8" />
              </>
            )}
            <p className="text-white/70 mb-8 text-lg leading-relaxed">
              Preencha os dados ao lado e receba um orçamento personalizado em <strong className="text-white">até 2 horas</strong>. Ou se preferir, fale diretamente com nossos especialistas.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-8 h-8 bg-[#E73336]/20 rounded-full flex items-center justify-center">
                  <CheckCircle size={18} weight="fill" className="text-[#E73336]" />
                </div>
                <span>Resposta rápida garantida</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-8 h-8 bg-[#E73336]/20 rounded-full flex items-center justify-center">
                  <CheckCircle size={18} weight="fill" className="text-[#E73336]" />
                </div>
                <span>Sem compromisso</span>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-8 h-8 bg-[#E73336]/20 rounded-full flex items-center justify-center">
                  <CheckCircle size={18} weight="fill" className="text-[#E73336]" />
                </div>
                <span>Atendimento personalizado</span>
              </div>
            </div>

            <motion.a
              href={WHATSAPP_QUOTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quote-whatsapp-direct"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] text-white font-bold text-sm uppercase tracking-wide rounded-full hover:bg-[#20BD5C] transition-all shadow-lg shadow-[#25D366]/30"
            >
              <WhatsappLogo weight="fill" size={24} />
              Falar com Especialista Agora
            </motion.a>
          </motion.div>

          <motion.div variants={fadeUp}>
            <form
              onSubmit={handleSubmit}
              data-testid="quote-form"
              className="bg-white p-8 sm:p-10 rounded-3xl shadow-2xl"
            >
              <h3 className="text-xl font-bold text-[#234D89] mb-6 text-center">
                Preencha para Orçamento
              </h3>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-[#2B2B2B] font-semibold mb-2 block text-sm">
                    Nome Completo *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    data-testid="form-input-name"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-[#E73336] focus:ring-0 bg-[#F5F5F5] transition-colors"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#2B2B2B] font-semibold mb-2 block text-sm">
                    WhatsApp *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="form-input-phone"
                    placeholder="(00) 00000-0000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-[#E73336] focus:ring-0 bg-[#F5F5F5] transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="material" className="text-[#2B2B2B] font-semibold mb-2 block text-sm">
                      Tipo de Material
                    </Label>
                    <Select
                      value={formData.material}
                      onValueChange={(value) => setFormData({ ...formData, material: value })}
                    >
                      <SelectTrigger
                        data-testid="form-select-material"
                        className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-[#F5F5F5] h-auto"
                      >
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="papel">Papel/Cartão</SelectItem>
                        <SelectItem value="embalagem">Embalagem</SelectItem>
                        <SelectItem value="revista">Revista/Catálogo</SelectItem>
                        <SelectItem value="folder">Folder/Flyer</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="quantity" className="text-[#2B2B2B] font-semibold mb-2 block text-sm">
                      Quantidade
                    </Label>
                    <Select
                      value={formData.quantity}
                      onValueChange={(value) => setFormData({ ...formData, quantity: value })}
                    >
                      <SelectTrigger
                        data-testid="form-select-quantity"
                        className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-[#F5F5F5] h-auto"
                      >
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ate-500">Até 500 un</SelectItem>
                        <SelectItem value="500-1000">500 a 1.000 un</SelectItem>
                        <SelectItem value="1000-5000">1.000 a 5.000 un</SelectItem>
                        <SelectItem value="5000-10000">5.000 a 10.000 un</SelectItem>
                        <SelectItem value="mais-10000">+10.000 un</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="service" className="text-[#2B2B2B] font-semibold mb-2 block text-sm">
                    Acabamento Desejado *
                  </Label>
                  <Select
                    value={formData.service}
                    onValueChange={(value) => setFormData({ ...formData, service: value })}
                  >
                    <SelectTrigger
                      data-testid="form-select-service"
                      className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-[#F5F5F5] h-auto"
                    >
                      <SelectValue placeholder="Selecione o acabamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <motion.button
                  type="submit"
                  data-testid="form-submit-button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full inline-flex items-center justify-center gap-3 px-8 py-5 bg-[#E73336] text-white font-bold text-base uppercase tracking-wide rounded-xl hover:bg-[#BD2A2C] transition-all shadow-lg shadow-[#E73336]/30 mt-2"
                >
                  <WhatsappLogo weight="fill" size={24} />
                  Ver Orçamento no WhatsApp
                </motion.button>

                <p className="text-center text-xs text-[#5C5C5C] mt-4">
                  Seus dados estão seguros e não serão compartilhados.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

export default QuoteForm;
